import React, { useEffect, useRef, useState } from 'react';
import { AMPM, HOURS_12, Time12 } from './dateutils';

/**
 * The scrolling time wheel — hour (12h) · minute (15-min steps) · AM/PM. A
 * centre highlight band marks the committed value; items fade as they move
 * from centre. Scroll/drag or click a value to spin. (Design tokens from the
 * "Date & time — picking states" spec.)
 */

const ACCENT = '#2f6fdb';
const MINUTES_STEP = [0, 15, 30, 45];

interface WheelColumnProps {
  values: (string | number)[];
  index: number;
  onIndex: (i: number) => void;
  itemH: number;
  colH: number;
  render: (v: string | number) => string;
  bold?: boolean;
  className?: string;
}

const WheelColumn: React.FC<WheelColumnProps> = ({
  values,
  index,
  onIndex,
  itemH,
  colH,
  render,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const settle = useRef<number | null>(null);
  const [visual, setVisual] = useState(index);
  const pad = (colH - itemH) / 2;

  // Snap to the committed value when it changes from outside this column
  // (or to finalise after a scroll settles).
  useEffect(() => {
    setVisual(index);
    const el = ref.current;
    if (!el) return;
    const target = index * itemH;
    if (Math.abs(el.scrollTop - target) > 1) el.scrollTop = target;
  }, [index, itemH]);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const i = Math.max(0, Math.min(values.length - 1, Math.round(el.scrollTop / itemH)));
    setVisual(i);
    if (settle.current) window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => {
      if (i !== index) onIndex(i);
    }, 120);
  };

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className={className}
      style={{
        position: 'relative',
        height: colH,
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <div style={{ height: pad }} />
      {values.map((v, i) => {
        const dist = Math.abs(i - visual);
        const style: React.CSSProperties = {
          height: itemH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          scrollSnapAlign: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          fontSize: dist === 0 ? 16 : dist === 1 ? 15 : 14,
          fontWeight: dist === 0 ? 800 : 400,
          color: dist === 0 ? ACCENT : dist === 1 ? '#7b8494' : '#c4ccd6',
        };
        return (
          <div
            key={i}
            style={style}
            onClick={() => onIndex(i)}
          >
            {render(v)}
          </div>
        );
      })}
      <div style={{ height: pad }} />
    </div>
  );
};

interface Props {
  time: Time12;
  onChange: (t: Time12) => void;
  /** "combined" is the narrower wheel inside the Date & time popover. */
  variant?: 'standalone' | 'combined';
}

const TimeWheelPanel: React.FC<Props> = ({
  time,
  onChange,
  variant = 'standalone',
}) => {
  const compact = variant === 'combined';
  const colH = compact ? 200 : 184;
  const itemH = compact ? 34 : 38;
  const bandH = compact ? 34 : 38;

  // Nearest 15-min step for the current minute (legacy odd minutes round in).
  const minIndex = Math.max(
    0,
    MINUTES_STEP.findIndex((m) => m >= time.minute) === -1
      ? MINUTES_STEP.length - 1
      : MINUTES_STEP.findIndex((m) => m >= time.minute),
  );

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        height: colH,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* centre highlight band */}
      <div
        style={{
          position: 'absolute',
          left: compact ? 8 : 10,
          right: compact ? 8 : 10,
          top: '50%',
          transform: 'translateY(-50%)',
          height: bandH,
          background: '#eef4fd',
          borderRadius: 9,
          pointerEvents: 'none',
        }}
      />
      <WheelColumn
        values={HOURS_12}
        index={time.hour - 1}
        onIndex={(i) => onChange({ ...time, hour: HOURS_12[i] })}
        itemH={itemH}
        colH={colH}
        render={(v) => String(v).padStart(2, '0')}
        className="flex-1"
      />
      {!compact && (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            fontSize: 16,
            fontWeight: 800,
            color: ACCENT,
          }}
        >
          :
        </div>
      )}
      <WheelColumn
        values={MINUTES_STEP}
        index={minIndex}
        onIndex={(i) => onChange({ ...time, minute: MINUTES_STEP[i] })}
        itemH={itemH}
        colH={colH}
        render={(v) => String(v).padStart(2, '0')}
        className="flex-1"
      />
      <div
        style={{
          position: 'relative',
          flex: 'none',
          width: compact ? 34 : 52,
          borderLeft: compact ? undefined : '1px solid #eef1f4',
        }}
      >
        <WheelColumn
          values={AMPM as unknown as string[]}
          index={time.ampm === 'AM' ? 0 : 1}
          onIndex={(i) => onChange({ ...time, ampm: AMPM[i] })}
          itemH={itemH}
          colH={colH}
          render={(v) => String(v)}
        />
      </div>
    </div>
  );
};

export default TimeWheelPanel;
